import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormError from '../partials/FormError';
import __ from '../../helpers/translation';
import { useCertificateStore } from '../../lib/stores/certificate';
import { reportStatus, useScheduledReportStore } from '../../lib/stores/scheduledReport';

const propTypes = {
  current: PropTypes.object.isRequired,
  removalType: PropTypes.string.isRequired,
  onAnswer: PropTypes.func
};

function CertificateCheckModal(props) {

  const {
    certificates,
    fetchAllCertificates,
    decodeCertificateData,
  } = useCertificateStore()

  // Fetch all certificates on initial render
  useEffect(() => {
    async function fetchCertificates() {
      await fetchAllCertificates()
    }
    fetchCertificates();
  }, []);

  const [ selectedCertificate, setSelectedCertificate ] = useState(null);
  const [ errors, setErrors ] = useState('');

  const { scheduledReports } = useScheduledReportStore();

  const parsedCertificates = useMemo(() => {

    const activeReport = scheduledReports.find(report => report.status === reportStatus.ACTIVE);
    if(!activeReport) {
      return [];
    }

    /** @type{number | undefined} */
    const location_id = activeReport?.data?.relation_ids?.location_id;
    if(!location_id) {
      return [];
    }

    /**
     * @type {Array<{ key: string, value: string }>}
     */
    const parsed = [];

    for(const certificate of certificates) {

      const { data, error } = decodeCertificateData(certificate.encodedData);
      if (error || !data) {
        console.error('Could not decode certificate data:', error);
        continue;
      }

      if(!data.removalTypes.includes(props.removalType) || !data.locationIds.includes(location_id)) {
        continue;
      }

      parsed.push({
        key: data.certificateId,
        value: data.workerName ? `${data.certificateId} - ${data.workerName}` : data.certificateId
      })
    }

    if(selectedCertificate === null && parsed.length) {
      setSelectedCertificate(parsed[0].key);
    }

    return parsed;

  }, [certificates])

  const performCheck = async () => {

    if(!selectedCertificate) {
      setErrors(__('app.general.select_certificate'));
      return;
    }

    if(!certificates.some(cert => cert.certificateId === selectedCertificate)) {
      setErrors(__('app.general.certificate_check_no_certificates_error'));
      return;
    }


    window.$('#modalCertificateNumber').modal('hide');
    props.onAnswer(selectedCertificate);
  }

  return (
    <div className="modal fade" id="modalCertificateNumber" tabIndex="-1" role="dialog" aria-labelledby="modalCertificateNumber" aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content p-3 p-sm-5">

          <div className="modal-header">
            <h1 className="modal-title fw-600" id="exampleModalLongTitle">
              { __('app.questions.certificate_check_title') }
              <span className="d-block font-size-18 m-0 p-0">
                { __('app.questions.certificate_check_subtitle') }
              </span>
            </h1>
            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className="modal-body">

            {errors && <FormError>{errors}</FormError> }

            {/* Shown dropdown / alert when no cert is found */}
            <div className="form-label-group">
              {!parsedCertificates.length ?
                <FormError>
                  { __('app.general.certificate_check_no_certificates') }
                </FormError>
              : (
                <select
                  className={'form-control'}
                  onFocus={() => fetchAllCertificates()}
                  value={selectedCertificate}
                  onChange={e => setSelectedCertificate(e.target.value)}
                  id="inputCertificate"
                >
                  {parsedCertificates.map((cert, idx) => (
                    <option key={idx} value={cert.key}>
                      {cert.value}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button
              type="button"
              onClick={performCheck}
              className="btn btn-success d-block mt-3 w-100"
            >
              { __('app.questions.certificate_check_button') }
            </button>

          </div>

        </div>
      </div>
    </div>
  );
}

CertificateCheckModal.propTypes = propTypes;

export default CertificateCheckModal;
